import React, { useMemo } from "react";
import classNames from "classnames";
import {
    Time as TimeConstants, isSameMonth, isSameWeek, tryParseDate,
} from "../../date";

interface TimeProps {
    date: Date | null | undefined;
    variant: "time" | "date" | "date-time";
    className?: string;
}

function isSameDate(date: Date, other: Date) {
    return date.getFullYear() === other.getFullYear()
        && date.getMonth() === other.getMonth()
        && date.getDate() === other.getDate();
}

function getDateTitle(date: Date) {
    const now = new Date();

    if (isSameDate(date, now)) {
        return "Today";
    }

    if (isSameDate(date, new Date(now.getTime() + TimeConstants.DAY))) {
        return "Tomorrow";
    }

    if (isSameWeek(date, now)) {
        return "This Week";
    }

    if (date.getTime() < now.getTime()) {
        if (isSameDate(date, new Date(now.getTime() - TimeConstants.DAY))) {
            return "Yesterday";
        }

        if (isSameWeek(date, new Date(now.getTime() - TimeConstants.WEEK))) {
            return "Last Week";
        }

        if (isSameMonth(date, now)) {
            return "Earlier this month";
        }

        if (isSameMonth(date, new Date(now.getTime() - TimeConstants.MONTH_30))) {
            return "Last Month";
        }

        if (date.getFullYear() === now.getFullYear()) {
            return "This year";
        }

        if (date.getFullYear() === now.getFullYear() - 1) {
            return "Last year";
        }

        return `${now.getFullYear() - date.getFullYear()} years ago`;
    }

    if (isSameWeek(date, new Date(now.getTime() + TimeConstants.WEEK))) {
        return "Next Week";
    }

    if (isSameMonth(date, now)) {
        return "This month";
    }

    if (isSameMonth(date, new Date(now.getTime() + TimeConstants.MONTH_30))) {
        return "Next Month";
    }

    if (date.getFullYear() === now.getFullYear()) {
        return "This year";
    }

    if (date.getFullYear() === now.getFullYear() + 1) {
        return "Next year";
    }

    return `${date.getFullYear() - now.getFullYear()} years from now`;
}
export default function Time({ date, variant, className }: TimeProps) {
    const parsed = tryParseDate(date);
    let time;
    if (variant === "time") {
        time = parsed?.toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "numeric",
        });
    } else if (variant === "date") {
        time = parsed?.toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    } else if (variant === "date-time") {
        time = parsed?.toLocaleString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
        });
    }

    const title = useMemo(() => (parsed ? getDateTitle(parsed) : undefined), [parsed]);
    return (
        <span className={classNames(className, `time-${variant}`)}>
            {parsed && variant === "date" && (title ? `${title}, ` : "")}
            {time}
        </span>
    );
}

Time.defaultProps = {
    className: "",
};
